import { Box, Popover, Stack } from '@mui/material';
import { Button, Dropdown } from 'src/UIKit';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import React, { useRef, useState } from 'react';
import {
  SurveyTimePeriod,
  SurveyTimePeriodFilterValue,
} from 'src/modules/surveyResponses/types/SurveyTimePeriod';

type SurveyTimePeriodFilterProps = {
  value: SurveyTimePeriodFilterValue;
  onChange: (value: SurveyTimePeriodFilterValue) => void;
};

const PERIOD_LABELS: Record<SurveyTimePeriod, string> = {
  allTime: 'All time',
  last7days: 'Last 7 days',
  last30days: 'Last 30 days',
  last90days: 'Last 90 days',
  last365days: 'Last 365 days',
  custom: 'Custom',
};

const PERIOD_OPTIONS = Object.entries(PERIOD_LABELS).map(([value, title]) => ({
  value: value as SurveyTimePeriod,
  title,
}));

const getFixedPeriodInterval = (period: SurveyTimePeriod): SurveyTimePeriodFilterValue => {
  const todayStart = dayjs().startOf('day');
  const endDateInterval = todayStart.add(1, 'day').toISOString();

  switch (period) {
    case 'last7days':
      return {
        timePeriod: period,
        startDateInterval: todayStart.subtract(7, 'days').toISOString(),
        endDateInterval,
      };
    case 'last30days':
      return {
        timePeriod: period,
        startDateInterval: todayStart.subtract(30, 'days').toISOString(),
        endDateInterval,
      };
    case 'last90days':
      return {
        timePeriod: period,
        startDateInterval: todayStart.subtract(90, 'days').toISOString(),
        endDateInterval,
      };
    case 'last365days':
      return {
        timePeriod: period,
        startDateInterval: todayStart.subtract(365, 'days').toISOString(),
        endDateInterval,
      };
    case 'custom':
      return {
        timePeriod: period,
        startDateInterval: null,
        endDateInterval: null,
      };
    case 'allTime':
    default:
      return {
        timePeriod: 'allTime',
        startDateInterval: null,
        endDateInterval: null,
      };
  }
};

const getCustomEndPickerValue = (endDateInterval: string | null) => {
  return endDateInterval ? dayjs(endDateInterval).subtract(1, 'day') : null;
};

const getCustomPeriodLabel = (value: SurveyTimePeriodFilterValue) => {
  const startDate = value.startDateInterval ? dayjs(value.startDateInterval).format('MM/DD/YY') : null;
  const endDate = getCustomEndPickerValue(value.endDateInterval)?.format('MM/DD/YY') ?? null;

  if (startDate && endDate) {
    return `${startDate} - ${endDate}`;
  }

  if (startDate) {
    return `From ${startDate}`;
  }

  if (endDate) {
    return `Until ${endDate}`;
  }

  return PERIOD_LABELS.custom;
};

const getPeriodLabel = (value: SurveyTimePeriodFilterValue) => {
  if (value.timePeriod === 'custom') {
    return getCustomPeriodLabel(value);
  }

  return PERIOD_LABELS[value.timePeriod];
};

export const SurveyTimePeriodFilter = ({ value, onChange }: SurveyTimePeriodFilterProps) => {
  const filterControlRef = useRef<HTMLDivElement | null>(null);
  const [customAnchorEl, setCustomAnchorEl] = useState<HTMLElement | null>(null);

  const handlePeriodChange = (period: SurveyTimePeriod) => {
    if (period === 'custom') {
      // Keep the currently applied dates so the user can fine-tune them
      // instead of wiping the selection every time "Custom" is picked.
      onChange({ ...value, timePeriod: 'custom' });
      setCustomAnchorEl(filterControlRef.current);
    } else {
      onChange(getFixedPeriodInterval(period));
      setCustomAnchorEl(null);
    }
  };

  const handlePeriodOptionClick = (period: SurveyTimePeriod) => {
    if (period === 'custom' && value.timePeriod === 'custom') {
      setCustomAnchorEl(filterControlRef.current);
    }
  };

  const handleStartDateChange = (date: Dayjs | null) => {
    onChange({
      ...value,
      timePeriod: 'custom',
      startDateInterval: date ? date.startOf('day').toISOString() : null,
    });
  };

  const handleEndDateChange = (date: Dayjs | null) => {
    onChange({
      ...value,
      timePeriod: 'custom',
      endDateInterval: date ? date.startOf('day').add(1, 'day').toISOString() : null,
    });
  };

  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
      <Box ref={filterControlRef} sx={{ width: { xs: '100%', sm: 190 } }}>
        <Dropdown
          label="Time Period"
          value={value.timePeriod}
          options={PERIOD_OPTIONS}
          onChange={(_, period) => handlePeriodChange(period)}
          onOptionClick={handlePeriodOptionClick}
          renderValue={() => getPeriodLabel(value)}
          margin="none"
          size="small"
        />
      </Box>
      <Popover
        open={Boolean(customAnchorEl)}
        anchorEl={customAnchorEl}
        onClose={() => setCustomAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack spacing={1.5} sx={{ p: 2, width: { xs: 280, sm: 360 } }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
              <DatePicker
                sx={{
                  width: { xs: '100%', sm: 160 },
                }}
                maxDate={getCustomEndPickerValue(value.endDateInterval) || undefined}
                format="MM/DD/YYYY"
                closeOnSelect
                onChange={handleStartDateChange}
                value={value.startDateInterval ? dayjs(value.startDateInterval) : null}
                slotProps={{
                  textField: {
                    placeholder: 'Start date',
                    size: 'small',
                  },
                }}
              />
              <DatePicker
                sx={{
                  width: { xs: '100%', sm: 160 },
                }}
                minDate={value.startDateInterval ? dayjs(value.startDateInterval) : undefined}
                format="MM/DD/YYYY"
                closeOnSelect
                onChange={handleEndDateChange}
                value={getCustomEndPickerValue(value.endDateInterval)}
                slotProps={{
                  textField: {
                    placeholder: 'End date',
                    size: 'small',
                  },
                }}
              />
            </Stack>
            <Stack direction="row" justifyContent="flex-end">
              <Button size="small" onClick={() => setCustomAnchorEl(null)}>
                Done
              </Button>
            </Stack>
          </Stack>
        </LocalizationProvider>
      </Popover>
    </Stack>
  );
};
